import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  CreateLocationFromAgreementProps,
  CreateServiceLocationFormModel,
} from './utility/types'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import { SideDrawerForm, LoadingSpinner } from '@sales-center/components'
import Stack from '@mui/material/Stack'
import { FormFields } from './FormFields'
import { CreateLocationHeader } from './CreateLocationHeader'
import { Form, useFormikContext } from 'formik'
import { useData } from './hooks/useData'
import { useHandlers } from './hooks/useHandlers'

interface Props {
  invalidLocations: CreateServiceLocationFormModel[]
  title: string
  containerTop?: string
  onCloseNewLocation: () => void
}

export const CreateLocationForm: FC<Props> = (props) => {
  const { invalidLocations, title, onCloseNewLocation, containerTop } = props

  const { onSubmit } = useHandlers()

  const [showOnlyRequired, setShowOnlyRequired] = useState<boolean>(false)

  const { loading, locationTypes, divisions, branches } = useData()

  const { values, isValidating, isSubmitting, errors, submitForm, isValid } =
    useFormikContext<CreateLocationFromAgreementProps>()

  const onChangeHideOptional = useCallback(
    (checked: boolean) => {
      setShowOnlyRequired(checked)
    },
    [setShowOnlyRequired],
  )

  useEffect(() => {
    const invalidEntries = Object.entries(values.locations).filter(
      ([_, { isInvalid }]) => isInvalid,
    )

    if ((!invalidEntries || invalidEntries.length === 0) && isValid) {
      submitForm()
      onCloseNewLocation()
    }
  }, [values.locations, submitForm, onCloseNewLocation, isValid])

  const disableSave = useMemo(() => {
    return (
      Object.keys(errors.locations ?? {}).length ==
        Object.entries(values.locations).filter(
          ([_, { isInvalid }]) => isInvalid,
        ).length ||
      Object.keys(errors.locations ?? {}).length > 0 ||
      isValidating ||
      isSubmitting
    )
  }, [errors.locations, values.locations, isValidating, isSubmitting])

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <SideDrawerForm
          onSave={onSubmit}
          primaryBtnLabel={'Save'}
          disableSave={disableSave}
          onCancel={onCloseNewLocation}
          containerTop={containerTop}
        >
          <Stack mb={2}>
            <CreateLocationHeader
              validatedLocations={invalidLocations}
              title={title}
              onChangeShowRequired={onChangeHideOptional}
              showOnlyRequired={showOnlyRequired}
            />

            <Form>
              <Box>
                {invalidLocations.map((location) => (
                  <Stack key={location.id}>
                    <Collapse
                      in={location.isInvalid}
                      timeout={{ exit: 1500 }}
                      unmountOnExit
                    >
                      <div key={location.id}>
                        <FormFields
                          location={location}
                          branches={branches}
                          locationTypes={locationTypes}
                          divisions={divisions}
                          hideOptional={showOnlyRequired}
                          path={`locations.${location.id}`}
                        />
                      </div>
                    </Collapse>
                  </Stack>
                ))}
              </Box>
            </Form>
          </Stack>
        </SideDrawerForm>
      )}
    </>
  )
}
CreateLocationForm.displayName = 'CreateLocationForm'
